/**
 * ScheduleMeetingDialog Component
 * 
 * Handles scheduling meetings with other users. Shows either:
 * 1. Existing meetings list (with cancel options) if user has scheduled meetings
 * 2. Meeting type selection and slot picker for new bookings
 * 
 * Features:
 * - Fetches unified schedule info (settings, meeting types, existing meetings)
 * - Filters meeting types based on outstandingPerPerson limits
 * - Applies availability constraints for maximumPerDay/maximumPerWeek
 * - Integrates with Cronofy for slot selection
 * - Handles meeting creation and cancellation
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeMediaQuery } from '@fuse/hooks';
import { format } from 'date-fns';

// Material UI Components
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip 
} from '@mui/material';

// Shared Components
import NativeDialog from 'app/shared-components/layout/NativeDialog';
import DefaultButton from '../layout/DefaultButton';
import SlotPickerWrapper from '../cronofy/SlotPickerWrapper';
import DateTimePickerWrapper from '../cronofy/DateTimePickerWrapper';

// Redux & API
import { selectScheduleMeetingDialogData, setScheduleMeetingDialogData } from 'app/store/schedulingSlice';
import { createMeeting, deleteMeeting } from 'app/store/meetingsSlice';
import { selectUser } from 'app/store/userSlice';
import { apiGet } from 'app/shared-components/util/restAPI';
import { getElementToken } from 'app/shared-components/util/cronofyApi';

// Utilities
import { errorMessage } from 'app/shared-components/util/message';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
import confirmationDialog from 'app/shared-components/util/confirmationDialog';

function ScheduleMeetingDialog() {
  // ===== HOOKS & STATE =====
  const dispatch = useDispatch();
  const fullScreen = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
  const dialogData = useSelector(selectScheduleMeetingDialogData);
  const currentUser = useSelector(selectUser);

  // Local state
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [cronofyOptions, setCronofyOptions] = useState(null);
  const [error, setError] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [loading, setLoading] = useState(true);
  const [showBookingView, setShowBookingView] = useState(false);

  // ===== DERIVED STATE =====
  const isOpen = !!dialogData;
  const targetUser = dialogData?.targetUser;
  const meetingTypes = scheduleInfo?.meetingTypes || [];
  const targetUserSettings = scheduleInfo?.userSettings;
  const scheduledMeetings = scheduleInfo?.scheduledMeetings || [];
  const hasExistingMeetings = scheduledMeetings.length > 0;

  // ===== UTILITY FUNCTIONS =====
  
  /**
   * Closes the dialog and resets state
   */
  const onClose = () => {
    dispatch(setScheduleMeetingDialogData(null));
  };

  /**
   * Fetches unified schedule information for the target user
   */
  const fetchScheduleData = async () => {
    setLoading(true);
    setError(false);
    setCronofyOptions(null);
    setSelectedMeetingType('');
    setScheduleInfo(null);

    try {
      const response = await apiGet('CoreAPI2', `/x2/userx/${targetUser.id}/scheduleinfo`, {});
      setScheduleInfo(response);
    } catch (error) {
      console.error('Failed to fetch schedule info:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filters meeting types based on outstandingPerPerson limits
   * Returns only meeting types where user hasn't exceeded the limit
   */
  const getAvailableMeetingTypes = () => {
    return meetingTypes.filter(meetingType => {
      // No limit set - always available
      if (!meetingType.outstandingPerPerson || meetingType.outstandingPerPerson === 0) {
        return true;
      }
      
      // Count existing meetings of this type
      const currentCount = scheduledMeetings.filter(meeting => 
        meeting.meetingTypeId === meetingType.id
      ).length;
      
      // Available if under the limit
      return currentCount < meetingType.outstandingPerPerson;
    });
  };

  /**
   * Builds availability constraints for Cronofy based on meeting type limits
   */
  const buildAvailabilityConstraints = (selectedType) => {
    const constraints = [];
    
    // Daily limit constraint
    if (selectedType?.maximumPerDay && selectedType.maximumPerDay > 0) {
      constraints.push({
        tags: { private: [{ value: 'MeetingType:' + selectedType.id }] },
        limit: selectedType.maximumPerDay,
        period: "day"
      });
    }
    
    // Weekly limit constraint
    if (selectedType?.maximumPerWeek && selectedType.maximumPerWeek > 0) {
      constraints.push({
        tags: { private: [{ value: 'MeetingType:' + selectedType.id }] },
        limit: selectedType.maximumPerWeek,
        period: "week"
      });
    }
    
    return constraints;
  };

  /**
   * Configures Cronofy options for slot selection
   */
  const setupCronofyOptions = async () => {
    setLoading(true);
    try {
      // Get Cronofy element token
      const response = await getElementToken(['availability'], [targetUser.id]);
      
      if (!response?.token) {
        console.error('No token in response:', response);
        setError(true);
        return;
      }

      const selectedType = meetingTypes.find(mt => mt.id === selectedMeetingType);
      const availabilityRuleIds = selectedType?.schedulingAvailabilityId ? [selectedType.schedulingAvailabilityId] : [];
      const schedulingWindowDays = selectedType?.schedulingWindow || 14;

      // Build member configuration with constraints
      const availabilityConstraints = buildAvailabilityConstraints(selectedType);
      const memberConfig = { 
        sub: targetUserSettings?.cronofySub,
        managed_availability: true,
        availability_rule_ids: availabilityRuleIds
      };
      
      if (availabilityConstraints.length > 0) {
        memberConfig.availability_constraints = availabilityConstraints;
      }

      // Configure Cronofy options
      const options = {
        element_token: response.token,
        availability_query: {
          participants: [{
            required: "all",
            members: [memberConfig]
          }],
          required_duration: { minutes: selectedType?.duration || 30 },
          query_periods: [{
            start: new Date().toISOString().split('.')[0] + 'Z', 
            end: new Date(Date.now() + schedulingWindowDays * 24 * 60 * 60 * 1000).toISOString().split('.')[0] + 'Z' 
          }]
        },
        callback: (data) => {
          if (data.notification?.type === 'slot_selected') {
            handleSlotSelected(data);
          } else if (data.notification?.type === 'no_slots_found') {
            handleNoSlotsFound();
          }
        }
      };
      
      setCronofyOptions(options);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch element token:', error);
      setError(true);
      setLoading(false);
    }
  };

  // ===== EVENT HANDLERS =====

  /**
   * Handles slot selection from Cronofy widget
   */
  const handleSlotSelected = (data) => {
    const slot = data.notification.slot;
    const selectedType = meetingTypes.find(mt => mt.id === selectedMeetingType);
    
    if (!selectedType || !selectedType.defaultEventInfo) {
      errorMessage(dispatch, 'Meeting type information not found. Please try again.');
      return;
    }
    
    // Prepare meeting data
    const meetingData = {
      meetingTypeId: selectedMeetingType,
      organizerUserId: targetUser.id,
      attendeeUserIds: [currentUser.id],
      eventInfo: {
        calendarId: selectedType.defaultEventInfo.calendarId,
        type: selectedType.defaultEventInfo.type,
        summary: selectedType.defaultEventInfo.summary || selectedType.name,
        description: selectedType.defaultEventInfo.description,
        start: slot.start,
        end: slot.end,
        location: selectedType.defaultEventInfo.location,
        conferencing: selectedType.defaultEventInfo.conferencing
      }
    };
    
    // Create the meeting
    dispatchSubmit({
      dispatch,
      submitFunction: createMeeting(meetingData),
      onSuccess: onClose,
      successMessage: 'Meeting scheduled successfully!'
    });
  };

  /**
   * Handles when no slots are found
   */
  const handleNoSlotsFound = () => {
    errorMessage(dispatch, 'No available meeting slots found.');
  };

  /**
   * Handles meeting cancellation with confirmation
   */
  const handleCancelMeeting = (meeting) => {
    confirmationDialog(dispatch, {
      title: 'Cancel Meeting',
      content: `Are you sure you want to cancel the meeting "${meeting.eventInfo?.summary}" scheduled for ${format(new Date(meeting.eventInfo?.start), 'PPpp')}?`,
      okText: 'Cancel Meeting',
      cancelText: 'Keep Meeting',
      onOk: () => {
        dispatchSubmit({
          dispatch,
          submitFunction: deleteMeeting({ id: meeting.id }),
          onSuccess: fetchScheduleData, // Refresh data after cancellation
          successMessage: 'Meeting cancelled successfully!'
        });
      },
      onCancel: () => {}
    });
  };

  // ===== EFFECTS =====

  /**
   * Fetch schedule data when dialog opens
   */
  useEffect(() => {
    if (isOpen && targetUser) {
      setShowBookingView(false); // Reset to existing meetings view
      fetchScheduleData();
    } else if (!isOpen) {
      setLoading(true);
      setScheduleInfo(null);
      setShowBookingView(false); // Reset state when dialog closes
    }
  }, [isOpen, targetUser]);

  /**
   * Auto-select first meeting type when available
   */
  useEffect(() => {
    if (meetingTypes.length > 0 && !selectedMeetingType) {
      setSelectedMeetingType(meetingTypes[0].id);
    }
  }, [meetingTypes, selectedMeetingType]);

  /**
   * Setup Cronofy options when ready and in booking view
   */
  useEffect(() => {
    if (isOpen && targetUser && selectedMeetingType && targetUserSettings && 
        meetingTypes.length > 0 && (!hasExistingMeetings || showBookingView)) {
      setupCronofyOptions();
    }
  }, [isOpen, targetUser, selectedMeetingType, meetingTypes, targetUserSettings, hasExistingMeetings, showBookingView]);

  // ===== COMPUTED VALUES =====
  const availableMeetingTypes = getAvailableMeetingTypes();
  const canScheduleMore = availableMeetingTypes.length > 0;
  const sortedScheduledMeetings = [...scheduledMeetings].sort((a, b) => 
    new Date(a.eventInfo?.start) - new Date(b.eventInfo?.start)
  );

  // ===== RENDER HELPERS =====

  /**
   * Renders the loading state
   */
  const renderLoading = () => (
    <div className="flex items-center justify-center h-64">
      <CircularProgress />
    </div>
  );

  /**
   * Renders error states
   */
  const renderError = () => (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <p className="text-red-600 mb-4">
          {meetingTypes.length === 0 
            ? 'No meeting types available. The user may not have scheduling configured.'
            : 'Unable to load schedule information. The user may not have scheduling enabled or configured.'}
        </p>
      </div>
    </div>
  );

  /**
   * Renders existing meetings list
   */
  const renderExistingMeetings = () => (
    <div className="flex-1">
      <Typography variant="h6" className="mb-16">Existing Meetings</Typography>
      <div className="space-y-12">
        {sortedScheduledMeetings.map((meeting) => {
          const meetingType = meetingTypes.find(mt => mt.id === meeting.meetingTypeId);
          return (
            <Card key={meeting.id} className="border">
              <CardContent className="flex justify-between items-center">
                <div>
                  <Typography variant="subtitle1" className="font-semibold">
                    {meeting.eventInfo?.summary || meetingType?.name || 'Meeting'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(meeting.eventInfo?.start), 'EEEE, PPpp')}
                  </Typography>
                  {meetingType && (
                    <Chip 
                      label={`${meetingType.name} (${meetingType.duration} min)`} 
                      size="small" 
                      className="mt-8"
                    />
                  )}
                </div>
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={() => handleCancelMeeting(meeting)}
                >
                  Cancel Meeting
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  /**
   * Renders meeting type selection
   */
  const renderMeetingTypeSelection = () => (
    <FormControl fullWidth className="mb-16">
      <InputLabel>Meeting Type</InputLabel>
      <Select
        value={selectedMeetingType}
        onChange={(e) => setSelectedMeetingType(e.target.value)}
        label="Meeting Type"
      >
        {availableMeetingTypes.map((meetingType) => (
          <MenuItem key={meetingType.id} value={meetingType.id}>
            {meetingType.name} ({meetingType.duration || 30} min)
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  /**
   * Renders slot picker
   */
  const renderSlotPicker = () => (
    <div className="flex-1">
      {fullScreen ? (
        <SlotPickerWrapper options={cronofyOptions} />
      ) : (
        <DateTimePickerWrapper options={cronofyOptions} />
      )}
    </div>
  );

  /**
   * Renders no available meeting types message
   */
  const renderNoMeetingTypes = () => (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <p className="text-red-600 mb-4">
          No meeting types available for booking. You may have reached the maximum number of scheduled meetings.
        </p>
      </div>
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <NativeDialog
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      width="800px"
      maxWidth="1000px"
    >
      <div className="px-16 flex-1 flex flex-col" style={{ minHeight: '400px' }}>
        {/* Header */}
        <div className="py-16">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-xl font-semibold">
              Schedule Meeting with {targetUser?.firstName} {targetUser?.lastName}
            </h3>
            {/* Toggle button when user has existing meetings but can schedule more */}
            {hasExistingMeetings && canScheduleMore && (
              <Button 
                size="small"
                variant="outlined"
                onClick={() => setShowBookingView(!showBookingView)}
              >
                {showBookingView ? 'View Existing' : 'New Meeting'}
              </Button>
            )}
          </div>
          <p className="text-gray-600 mb-16">
            {hasExistingMeetings && !showBookingView
              ? 'You have existing meetings scheduled with this person. You can cancel them below.'
              : 'Select a meeting type and available time slot to schedule your meeting.'}
          </p>

          
          {/* Meeting Type Selection - show if booking view or no existing meetings */}
          {(!hasExistingMeetings || showBookingView) && availableMeetingTypes.length > 0 && renderMeetingTypeSelection()}
        </div>

        {/* Main Content */}
        {loading ? renderLoading() :
         error || meetingTypes.length === 0 ? renderError() :
         hasExistingMeetings && !showBookingView ? renderExistingMeetings() :
         availableMeetingTypes.length === 0 ? renderNoMeetingTypes() :
         cronofyOptions && selectedMeetingType ? renderSlotPicker() : null}
      </div>

      {/* Footer */}
      <div className="flex justify-end py-16 sm:py-24 px-24">
        <DefaultButton text={hasExistingMeetings && !showBookingView ? "Close" : "Cancel"} onClick={onClose} />
      </div>
    </NativeDialog>
  );
}

export default ScheduleMeetingDialog;